import { Member } from '~/types';

export interface TextSegment {
  italic: boolean;
  text: string;
}

export const parseTextWithItalics = (text: string): TextSegment[] => {
  if (!text) return [];
  const output: TextSegment[] = [];
  const lineBreakRegex = /\r\n|\r|\n/;
  const italicRegex = /\*(.*?)\*/g;
  const directionRegex = /\*direction:\s*([^*]+)\*/g;

  // Preprocess the text to replace *direction: ~~~* with *~~~*
  const preprocessedText = text.replace(directionRegex, '*$1*');
  if (!preprocessedText) return [];

  // Split the text by line breaks
  const lines = preprocessedText.split(lineBreakRegex);

  lines.forEach((line, index) => {
    let lastIndex = 0;
    let match;

    while ((match = italicRegex.exec(line)) !== null) {
      // Add non-italic text before the italic segment
      if (match.index > lastIndex) {
        const nonItalicText = line.slice(lastIndex, match.index).trim();
        if (nonItalicText) {
          output.push({ italic: false, text: nonItalicText });
        }
      }

      // Add the italic segment
      output.push({ italic: true, text: match[1].trim() });

      lastIndex = italicRegex.lastIndex;
    }

    // Add any remaining non-italic text after the last italic segment
    const remainingText = line.slice(lastIndex).trim();
    if (remainingText) {
      output.push({ italic: false, text: remainingText });
    }

    // Add line break info if it's not the last line
    if (index < lines.length - 1) {
      output.push({ italic: false, text: '\n' });
    }
  });

  return output;
};

export const parseTextWithItalics2 = (text: string): TextSegment[] => {
  if (!text) return [];
  const output: TextSegment[] = [];
  const lineBreakRegex = /\r\n|\r|\n/;
  const italicRegex = /\*(.*?)\*/g;
  const directionRegex = /\*direction:\s*([^*]+)\*/g;

  // Preprocess the text to replace *direction: ~~~* with *~~~*
  const preprocessedText = text.replace(directionRegex, '*$1*');
  if (!preprocessedText) return [];

  // Split the text by line breaks
  const lines = preprocessedText.split(lineBreakRegex);

  lines.forEach((line, index) => {
    let lastIndex = 0;
    let match;

    while ((match = italicRegex.exec(line)) !== null) {
      // Add non-italic text before the italic segment
      if (match.index > lastIndex) {
        const nonItalicText = line.slice(lastIndex, match.index).trim();
        if (nonItalicText) {
          // Further split non-italic text based on punctuation, keeping punctuation attached
          splitNonItalicText(nonItalicText).forEach((segment) =>
            output.push(segment),
          );
        }
      }

      // Add the italic segment
      output.push({ italic: true, text: match[1].trim() });

      lastIndex = italicRegex.lastIndex;
    }

    // Add any remaining non-italic text after the last italic segment
    const remainingText = line.slice(lastIndex).trim();
    if (remainingText) {
      splitNonItalicText(remainingText).forEach((segment) =>
        output.push(segment),
      );
    }

    // Add line break info if it's not the last line
    if (index < lines.length - 1) {
      output.push({ italic: false, text: '\n' });
    }
  });

  return output;
};

// Helper function to further split non-italic text by punctuation, keeping punctuation attached
const splitNonItalicText = (text: string): TextSegment[] => {
  const segments: TextSegment[] = [];
  let lastIndex = 0;
  let match;

  const splitRegex = /(?<!Dr)(\s*[.!?]+)(?=\s|$)/g; // Matches punctuation but avoids splitting after "Dr."

  // Use the splitRegex to split by punctuation but keep punctuation attached
  while ((match = splitRegex.exec(text)) !== null) {
    const endIndex = match.index + match[0].length;

    // Add text including punctuation
    if (match.index > lastIndex) {
      const fullSegment = text.slice(lastIndex, endIndex).trim();
      if (fullSegment) {
        segments.push({ italic: false, text: fullSegment });
      }
    }

    lastIndex = endIndex;
  }

  // Add any remaining text after the last punctuation
  const remainingText = text.slice(lastIndex).trim();
  if (remainingText) {
    segments.push({ italic: false, text: remainingText });
  }

  return segments;
};

export const getMemberName = (members: Member[], id: string | undefined) => {
  if (!id) return '';
  const member = members.find((member) => member.id === id);
  return member ? member.name : '';
};

export const parseLikeNumber = (count: number) => {
  if (count < 1000) {
    return count.toString();
  } else if (count >= 1000 && count < 1000000) {
    const thousands = count / 1000;
    if (Number.isInteger(thousands)) {
      return `${thousands}K`;
    }
    return `${Math.floor(thousands)}.${Math.floor((count % 1000) / 100)}K`;
  } else if (count >= 1000000 && count < 1000000000) {
    const millions = count / 1000000;
    if (Number.isInteger(millions)) {
      return `${millions}M`;
    }
    return `${Math.floor(millions)}.${Math.floor((count % 1000000) / 100000)}M`;
  } else {
    return '999M+';
  }
};
